import { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { LuUserRound } from "react-icons/lu";
import { useNavigate } from 'react-router-dom';
import Card from 'react-bootstrap/Card';
import { Button } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { Input, Alert } from 'antd';
import axiosInstance from '../../services/axiosConfig';
import TransText from "../../translation/trans_tag";
import { FaRegSquare, FaRegCheckSquare } from "react-icons/fa";

const passwordRequirement = [
  {
    label: "Almeno 8 caratteri",
    test: (pw) => pw.length >= 8,
  },
  {
    label: "Almeno una lettera maiuscola",
    test: (pw) => /[A-Z]/.test(pw),
  },
  {
    label: "Almeno un numero",
    test: (pw) => /\d/.test(pw),
  },
  {
    label: "Almeno un carattere speciale",
    test: (pw) => /[!@#$%^&*()_+\-=\[\]{};':\"\\|,.<>\/?]/.test(pw),
  },
];

const Settings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordChecks, setPasswordChecks] = useState(passwordRequirement ? passwordRequirement.map(() => false) : []);

  const [fields, setFields] = useState({
    name: '',
    surname: '',
    email: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setFields({
          name: payload.firstName || '',
          surname: payload.lastName || '',
          email: payload.email || ''
        });
      } catch (e) {
        setFields({ name: '', surname: '', email: '' });
      }
    }
  }, []);

  const [passwordRequirements, setPasswordRequirements] = useState('');
  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      setPasswordError(true);
      setErrorMessage("Le password non corrispondono");
      return;
    }

    setIsLoading(true);
    setPasswordError(false);
    setErrorMessage('');
    setPasswordRequirements('');

    try {
      const response = await axiosInstance.post('/auth/change-password', {
        currentPassword: oldPassword,
        newPassword: newPassword
      });

      if (response.data.success) {
        setShowConfirmation(true);
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordError(true);
        setErrorMessage(response.data.message || "La password attuale non è corretta");
      }
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordError(true);

      // Common error messages we expect from the backend
      const knownErrors = [
        'Password attuale non valida',
        'Password attuale non corretta',
        'La nuova password deve essere diversa dalla precedente',
        'Password non valida'
      ];
      
      // Check if error contains one of our known backend messages
      let errorMsg = '';
      
      // Get the exact error message from the backend
      if (error.response && error.response.data) {
        // Check for the error message in various locations
        if (error.response.data.error) {
          errorMsg = error.response.data.error;
        } else if (error.response.data.message) {
          errorMsg = error.response.data.message;
        } else if (typeof error.response.data === 'string') {
          errorMsg = error.response.data;
          
          // If it's a 500 error with the full stack trace
          if (errorMsg.includes('Error:')) {
            // Try to extract just the error message part
            for (const knownErr of knownErrors) {
              if (errorMsg.includes(knownErr)) {
                errorMsg = knownErr;
                break;
              }
            }
          }
        }
        
        // Set the error message
        setErrorMessage(errorMsg || 'Errore durante il cambio password');
        
        // Handle password requirements if they exist
        if (error.response.data.failedRequirements) {
          setPasswordRequirements(error.response.data.failedRequirements.join('<br />'));
        }
      } else if (error.message) {
        // Direct error message from JavaScript
        setErrorMessage(error.message);
      } else {
        setErrorMessage("Errore di connessione al server");
      }
    } finally {
      setIsLoading(false);
    }
  }; return (
    <>
      <header className="text-center p-3" style={{ backgroundColor: 'var(--second-color-extra-light2)', borderBottom: '1px solid #ddd' }}>
        <div className="d-flex flex-column align-items-center">
          <LuUserRound className="color-2 text-white icon-size-settings p-3 mt-1 mb-1 rounded-circle" />
          <h3>{t('general.settings.hello')} {fields.name} {fields.surname}</h3>
          <div className='mx-5'><div className="fs-5 ms-5 me-5">{t('general.settings.description')}</div></div>
        </div>
      </header>
      <div className ="container-xxl">
        <Row className="justify-content-center mt-3">
          <Col md={6} lg={4}>
            <Card
              className="settings-card"
              onMouseEnter={() => setHoveredIndex(0)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <Card.Body>
                <Card.Title className="fs-5">{t('general.settings.user')}</Card.Title>
                <Card.Text className="fs-6">{t('general.settings.userText')}</Card.Text>
                {!showConfirmation && (
                  <>
                    <p className="fs-6 mt-1">{t('general.login.oldPassword')}
                      <Input.Password
                        className="text-M"
                        placeholder={t('general.login.oldPasswordPlaceholder')}
                        iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                        value={oldPassword}
                        onChange={e => setOldPassword(e.target.value)}
                      />
                    </p>
                    <p className="fs-6 mt-1">{t('general.login.newPassword')}
                      <Input.Password
                        className="text-M"
                        placeholder={t('general.login.newPasswordPlaceholder')}
                        iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                        value={newPassword}
                        onChange={e => {
                          setNewPassword(e.target.value);
                          // Aggiorna dinamicamente i check come in Login.js
                          setPasswordChecks(passwordRequirement.map(req => req.test(e.target.value)));
                        }}
                      />
                      {newPassword && (
                        <div className="mt-2 fs-6 text-muted">
                          <div>La password deve contenere:</div>
                          <ul style={{ marginBottom: 0, marginTop: 4, paddingLeft: 20, listStyle: "none" }}>
                            {passwordRequirement.map((req, idx) => (
                              <li key={req.label} className='d-flex align-items-center'>
                                {passwordChecks[idx] ? (
                                  <FaRegCheckSquare
                                    className="me-2"
                                    style={{ fontSize: "1.2em", minWidth: "1.2em" }}
                                  />
                                ) : (
                                  <FaRegSquare
                                    className="me-2 text-muted"
                                    style={{ fontSize: "1.2em", minWidth: "1.2em" }}
                                  />
                                )}
                                {req.label}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </p>
                    <p className="fs-6 mt-1">{t('general.login.confirmPassword')}
                      <Input.Password
                        className="text-M"
                        placeholder={t('general.login.newPasswordPlaceholder')}
                        iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                      />
                      {newPassword && (
                        <div className="mt-2 fs-6 text-muted">
                          <ul style={{ marginBottom: 0, marginTop: 4, paddingLeft: 20, listStyle: "none" }}>
                            <li className='d-flex align-items-center'>
                              {confirmPassword && newPassword && confirmPassword === newPassword ? (
                                <FaRegCheckSquare
                                  className="me-2"
                                  style={{ fontSize: "1.3em", minWidth: "1.3em" }}
                                />
                              ) : (
                                <FaRegSquare
                                  className="me-2 text-muted"
                                  style={{ fontSize: "1.3em", minWidth: "1.3em" }}
                                />
                              )}
                              Le password corrispondono
                            </li>
                          </ul>
                        </div>
                      )}
                    </p>
                    {passwordError && (
  <Alert
    message={
      <div className='fs-6'>
        {errorMessage}
      </div>
    }
    description={
      passwordRequirements && (
        <div>
          <ul className="fs-6">
            {passwordRequirements.split('<br />').map((requirement, index) => (
              <li key={index}>
                {requirement}
              </li>
            ))}
          </ul>
        </div>
      )
    }
    type="error"
    showIcon
    className="mb-3"
  />
)}

                    <div className="d-flex justify-content-center align-items-center">
                      <Button
                        type='primary'
                        className='button-responsive fs-5 disabled-button button-color'
                        onClick={handlePasswordChange}
                        disabled={!oldPassword || !newPassword || !confirmPassword || isLoading}
                        loading={isLoading}
                      >
                        {t('general.actions.confirm')}
                      </Button>
                    </div>
                  </>
                )}
                {showConfirmation && (
                  <>
                  <Alert
                    showIcon
                    type='success'
                    className='mt-3 fs-6'
                    message={
                      <TransText i18nKey={'general.settings.changed'} />
                     }
                  />
                    <div className="d-flex justify-content-center mt-3">
                      <Button
                        type="primary"
                        className="button-responsive fs-5 button-color"
                        onClick={() => setShowConfirmation(false)}
                      >
                        Modifica password
                      </Button>
                    </div>
                    </>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col md={6} lg={4}>
            <Card
              className="settings-card"
              onMouseEnter={() => setHoveredIndex(1)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <Card.Body>
                <Card.Title className="fs-5">{t('general.settings.data')}</Card.Title>
                <Card.Text className="fs-6">{t('general.settings.dataTitle')}</Card.Text>
                <div className="d-flex flex-column gap-2 mb-2 fs-6">
                  <span><span className="fw-bold">{t('general.user.name')}:</span> {fields.name}</span>
                  <span><span className="fw-bold">{t('general.user.surname')}:</span> {fields.surname}</span>
                  <span><span className="fw-bold">{t('general.user.email')}:</span> {fields.email}</span>
                </div>

                <Alert
                  showIcon
                  type='info'
                  className='mt-3 fs-6'
                  message={<TransText  i18nKey='general.settings.info' 
                  customComponents={{
              Link: <a className ="icon-cursor" 
              style={{ textDecoration: 'underline' }} 
              onClick={() => navigate('/issues')} />
            }}/>} 
                  
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default Settings;
